import { ContactUsField } from "./types";
import {
    DomainInfo,
    validateField as serverSideFieldValidation,
} from "src/api";
import { getServerSideValidationErrorMessage } from "@lib/shared/form";
import { AxiosError } from "axios";
import { capitalizeFirstLetter } from "@lib/capitalizeFirstLetter";
export const mainContactUsForm: { [x: string]: ContactUsField } = {
    firstName: {
        name: "firstName",
        label: "First Name",
        placeholder: "First Name",
        inputType: "text",
        type: "text",
        validation: {
            required: true,
            rules: {
                type: "isEmpty",
            },
        },
        autocomplete: "given-name",
        serverSideValidation: true,
    },
    lastName: {
        name: "lastName",
        label: "Last Name",
        placeholder: "Last Name",
        inputType: "text",
        type: "text",
        validation: {
            required: true,
            rules: {
                type: "isEmpty",
            },
        },
        autocomplete: "family-name",
        serverSideValidation: true,
    },
    email: {
        name: "email",
        label: "Email",
        placeholder: "Email",
        inputType: "text",
        type: "email",
        validation: {
            required: true,
            rules: {
                type: "isEmail",
            },
        },
        autocomplete: "email",
        serverSideValidation: true,
    },
    phoneNumber: {
        name: "phone",
        label: "Phone Number",
        placeholder: "Phone Number",
        inputType: "text",
        type: "tel",
        validation: {
            required: true,
            rules: {
                type: "isPhone",
            },
        },
        autocomplete: "tel",
    },
    subject: {
        name: "subject",
        label: "Subject",
        placeholder: "Subject",
        inputType: "text",
        type: "text",
        validation: {
            required: true,
            rules: {
                type: "isEmpty",
                characterCount: {
                    min: 3,
                    max: 255,
                },
            },
        },
    },
    description: {
        name: "body",
        label: "Description",
        placeholder:
            "Please enter the details of your request. A member of our support staff will get back to you within 24 hours",
        inputType: "textarea",
        type: null,
        rows: 5,
        validation: {
            required: true,
            rules: {
                type: "isEmpty",
                characterCount: {
                    min: 5,
                    max: 255,
                },
            },
        },
    },
    recaptcha: {
        name: "googleReCaptcha",
        label: "",
        placeholder: "",
        inputType: "recaptcha",
        type: null,
        validation: {
            required: true,
        },
    },
};

const getErrorMessage = (
    fieldName: string,
    errorCode: string,
    locale: string,
    options: { maxCount?: number; minCount?: number } = {},
) => {
    switch (errorCode) {
        case "REQUIRED":
            switch (locale) {
                case "ar":
                    return `${fieldName} مطلوب`;
                case "es":
                    return capitalizeFirstLetter(`${fieldName} es requerido`);
                case "en":
                default:
                    return capitalizeFirstLetter(`${fieldName} is required`);
            }
        case "INVALID_EMAIL":
            switch (locale) {
                case "ar":
                    return `يرجى إدخال ${fieldName} صالح`;
                case "es":
                    return capitalizeFirstLetter(
                        `Por favor, introduzca un ${fieldName} válido`,
                    );
                case "en":
                default:
                    return capitalizeFirstLetter(
                        `Please enter a valid ${fieldName}`,
                    );
            }
        case "INVALID_PHONE":
            switch (locale) {
                case "ar":
                    return `يرجى إدخال ${fieldName} صالح`;
                case "es":
                    return capitalizeFirstLetter(
                        `Por favor, introduzca un ${fieldName} válido`,
                    );
                case "en":
                default:
                    return capitalizeFirstLetter(
                        `Please enter a valid ${fieldName}`,
                    );
            }
        case "INVALID_STREET_ADDRESS":
            switch (locale) {
                case "ar":
                    return `يرجى إدخال ${fieldName} صالح`;
                case "es":
                    return capitalizeFirstLetter(
                        `Por favor, introduzca un ${fieldName} válido`,
                    );
                case "en":
                default:
                    return capitalizeFirstLetter(
                        `Please enter a valid ${fieldName}`,
                    );
            }

        case "MAX_LENGTH_EXCEEDED":
            switch (locale) {
                case "ar":
                    return `${fieldName} لا يمكن أن يكون أكثر من ${
                        options?.maxCount?.toString() as string
                    } أحرف`;
                case "es":
                    return capitalizeFirstLetter(
                        `${fieldName} no puede tener más de ${
                            options?.maxCount?.toString() as string
                        } caracteres`,
                    );
                case "en":
                default:
                    return capitalizeFirstLetter(
                        `${fieldName} cannot be more than ${
                            options?.maxCount?.toString() as string
                        } characters`,
                    );
            }
        case "MIN_LENGTH_NOT_MET":
            switch (locale) {
                case "ar":
                    return `يرجى إدخال ما لا يقل عن ${
                        options?.minCount?.toString() as string
                    } أحرف`;
                case "es":
                    return capitalizeFirstLetter(
                        `Por favor, introduzca por lo menos ${
                            options?.minCount?.toString() as string
                        } caracteres`,
                    );
                case "en":
                default:
                    return capitalizeFirstLetter(
                        `Please enter at least ${
                            options?.minCount?.toString() as string
                        } characters`,
                    );
            }

        default:
            return capitalizeFirstLetter(`Invalid ${fieldName}`);
    }
};

export const fieldValidation = async (
    field: ContactUsField,
    value: string,
    domain: DomainInfo,
    locale: string,
): Promise<{ valid: boolean; message: string }> => {
    const conditions = [];

    const fieldName = field.errorMessageLabel || field.label || field.name;

    if (!value) {
        if (field.validation.required) {
            const fieldLabel = {
                en: "This field",
                es: "Este campo",
                // cspell:disable-next-line
                ar: "هذا الحقل",
            };
            conditions.push({
                valid: false,
                message: getErrorMessage(
                    fieldLabel[locale as keyof typeof fieldLabel] ||
                        fieldLabel["en"],
                    "REQUIRED",
                    locale,
                ),
            });
        }
    } else {
        if (field.validation.rules?.type === "isEmail") {
            const reg = new RegExp(
                /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
            );

            const isValid = reg.test(String(value).toLowerCase());

            !isValid &&
                conditions.push({
                    valid: false,
                    message: getErrorMessage(
                        fieldName,
                        "INVALID_EMAIL",
                        locale,
                    ),
                });
        }

        if (field.validation.rules?.type === "isPhone") {
            const reg = new RegExp(
                /^\s*(?:\+?(\d{1,3}))?[-. (]*(\d{3})[-. )]*(\d{3})[-. ]*(\d{4})(?: *x(\d+))?\s*$/,
            );
            const isValid = reg.test(String(value).toLowerCase());

            !isValid &&
                conditions.push({
                    valid: false,
                    message: getErrorMessage(
                        fieldName,
                        "INVALID_PHONE",
                        locale,
                    ),
                });
        }
        if (field.validation.rules?.type === "isStreetAddress") {
            // must start with a number and contain at least one letter
            const reg = new RegExp(/^\s*\d+\s+([a-zA-Z0-9\s\.,#\-]+)\s*$/);
            const isValid = reg.test(String(value).toLowerCase());

            !isValid &&
                conditions.push({
                    valid: false,
                    message: getErrorMessage(
                        fieldName,
                        "INVALID_STREET_ADDRESS",
                        locale,
                    ),
                });
        }

        if (field.validation.rules?.characterCount?.max) {
            const isValid =
                value.trim().length <=
                field.validation.rules?.characterCount?.max;
            !isValid &&
                conditions.push({
                    valid: false,
                    message: getErrorMessage(
                        fieldName,
                        "MAX_LENGTH_EXCEEDED",
                        locale,
                        {
                            maxCount:
                                field.validation.rules?.characterCount?.max,
                        },
                    ),
                });
        }

        if (field.validation.rules?.characterCount?.min) {
            const isValid =
                value.trim().length >=
                field.validation.rules?.characterCount?.min;
            !isValid &&
                conditions.push({
                    valid: false,
                    message: getErrorMessage(
                        fieldName,
                        "MIN_LENGTH_NOT_MET",
                        locale,
                        {
                            minCount:
                                field.validation.rules?.characterCount?.min,
                        },
                    ),
                });
        }
    }

    if (conditions.length >= 1) {
        return { valid: false, message: conditions[0].message };
    }
    if (field.serverSideValidation) {
        const { error } = await serverSideFieldValidation({
            ruleName: field.name,
            value,
        });
        if (error) {
            return {
                valid: false,
                message: capitalizeFirstLetter(
                    getServerSideValidationErrorMessage(
                        error as AxiosError<{
                            validationMessage?: string;
                            message?: string;
                            errorCode?: string;
                        }>,
                        fieldName,
                        domain,
                        locale,
                    ),
                ),
            };
        }
    }
    const valid = true;
    const message = "";
    return { valid, message };
};
